import React, { useState, useEffect } from "react";
import "./ServiceDetails.css";
import Widgets from "../../../../widgets";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import strings from "../../../../utils/strings";
import PatientDetails from "../patientDetails/PatientDetails";
import ServiceConfiguration from "../serviceConfiguration/ServiceConfiguration";
import NursePreferences from "../nursePref/NursePreferences";
import QuotationDetails from "../quotation/QuotationDetails";
import { getNurseRequestDetails, verifyNurseRequest, assignNurseRequest } from "../../../../modules/NursignCare/ManageRequest/manageRequestThunk";
import { buildFileUrl, getFileNameFromUrl, getFileType } from "../../../../utils/fileHelpers";

const ServiceDetails = ({ setSelectedView }) => {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const dispatch = useDispatch();
  const { requestDetails, newRequests } = useSelector((state) => state.manageRequest);

  // newRequests already has patientCondition from the list API — use as immediate fallback
  const requestFromList = newRequests?.find((r) => r._id === id);

  const breadcrumbs = ["nursingCare", "manageRequests", "serviceDetails"];
  const paths = [strings.navigation.manageNurseReq, strings.navigation.manageNurseReq, strings.navigation.viewNurseReqDetails];
  const steps = [t("patientDetails"), t("serviceDetails"), t("nursePref"), t("quotation")];
  const [enabledSteps, setEnabledSteps] = useState([1]);
  const [currentStep, setCurrentStep] = useState(1);
  const [page, setPage] = useState(1);

  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (id) dispatch(getNurseRequestDetails({ id }));
  }, [id]);

  useEffect(() => {
    setCurrentStep(page);
  }, [page]);

  const formatAddress = (addr) => {
    if (!addr) return "";
    if (typeof addr === "string") return addr;
    return [addr.street, addr.ward, addr.city, addr.province, addr.postalCode].filter(Boolean).join(", ");
  };

  const sanitizeAddress = (addr) => {
    if (!addr || typeof addr === "string") return null;

    const address = {
      personalCountryCode: String(addr.personalCountryCode || ""),
      personalPhoneNumber: String(addr.personalPhoneNumber || ""),
      street: String(addr.street || ""),
      ward: String(addr.ward || ""),
      district: String(addr.district || ""),
      postalCode: String(addr.postalCode || ""),
      city: String(addr.city || ""),
      province: String(addr.province || ""),
    };

    if (addr.officeCountryCode && addr.officePhoneNumber) {
      address.officeCountryCode = String(addr.officeCountryCode);
      address.officePhoneNumber = String(addr.officePhoneNumber);
    }

    return address;
  };

  // Some requests carry patient info under `patient`, others (booked by a relative) under `relativeInfo`
  const person = requestDetails?.patient || requestDetails?.relativeInfo || {};

  const patientDetailsData = {
    name: person?.fullName || `${person?.firstName || ""}${person?.lastName ? " " + person.lastName : ""}`.trim() || "",
    gender: person?.gender || "",
    hnNumber: person?.hnNumber || "",
    insuranceType: person?.insuranceCategory || "",
    phone: person?.countryCode ? `+${person.countryCode} ${person.phoneNumber}` : person?.phoneNumber || "",
    email: person?.email || "",
    address: formatAddress(requestDetails?.address),
    addressFields: formData?.addressFields ?? requestDetails?.address,
    dateOfBirth: person?.dateOfBirth || "",
    attachments: (requestDetails?.attachments || []).map((a) => {
      if (typeof a === "string") {
        const name = getFileNameFromUrl(a) || "file";
        return { type: getFileType({ name }), url: buildFileUrl(a), name };
      }
      const rawUrl = a.url || a.path || a.fileUrl || a.fileURL || a.filePath || a.location || a.signedUrl || a.publicUrl || a.attachmentUrl || a.documentUrl || "";
      const url = buildFileUrl(rawUrl);
      const name = a.name || a.fileName || a.originalName || getFileNameFromUrl(rawUrl) || "file";
      return { type: getFileType({ name, mimeType: a.mimeType || a.mimetype || a.contentType || a.type }), url, name };
    }),
    description: formData?.description ?? requestDetails?.description ?? "",
    patientCondition: formData?.patientCondition ?? requestDetails?.patientCondition ?? requestFromList?.patientCondition ?? "",
    distanceInKm: formData?.distanceInKm ?? requestDetails?.distanceInKm ?? requestFromList?.distanceInKm ?? requestDetails?.zone ?? requestDetails?.address?.zone ?? "",
    profilePhoto: person?.profilePhoto || "",
    status: requestDetails?.status || requestFromList?.status || "OPEN",
  };

  // Step 1 — POST assign API (Review API)
  const handleStep1Review = ({ patientCondition, addressFields, addressText, description, zone }) => {
    return new Promise((resolve, reject) => {
      const distanceInKm = parseFloat(zone) || 0;
      const address = sanitizeAddress(addressFields);
      setFormData((prev) => ({ ...prev, patientCondition, addressFields: address || addressFields, address, addressText, description, zone, distanceInKm }));

      const body = {
        distanceInKm,
        ...(patientCondition ? { patientCondition } : {}),
        ...(address ? { address } : {}),
        ...(description ? { description } : {}),
      };

      dispatch(assignNurseRequest({ id, body })).then((result) => {
        if (result.meta.requestStatus === "fulfilled") {
          // If needed, we can trigger a refetch of request details here
          // dispatch(getNurseRequestDetails({ id }));
          resolve(result);
        } else {
          import("../../../../widgets/Toast/toastPopup").then(({ default: ToastPopup }) => ToastPopup.error(result.error?.message || "Review failed"));
          reject(result.error);
        }
      });
    });
  };

  const handleStep1Next = ({ patientCondition, addressFields, addressText, description, zone }) => {
    const distanceInKm = parseFloat(zone) || 0;
    const address = sanitizeAddress(addressFields);
    setFormData((prev) => ({ ...prev, patientCondition, addressFields: address || addressFields, address, addressText, description, zone, distanceInKm }));

    setEnabledSteps((prev) => [...new Set([...prev, 2])]);
    setPage(2);
  };

  const handleStep2Save = ({ services, visits, startDate, endDate, frequencyMap }) => {
    setFormData((prev) => ({ ...prev, services, visits, startDate, endDate, frequencyMap }));
  };

  const handleStep3Save = ({ languagePreferences, genderPreferences }) => {
    setFormData((prev) => ({ ...prev, languagePreferences, genderPreferences }));
  };

  const handleFinalSubmit = ({ quotationURL, price, quotationItems }) => {
    const uploadedQuotationURL = quotationURL?.trim() || "";
    const accumulated = { ...formData, quotationURL: uploadedQuotationURL, price, quotationItems };
    const firstVisit = accumulated.visits?.[0] || {};

    // Use edited services from Step 2 when present; fall back to GET API services.
    const sourceServices = accumulated.services?.length ? accumulated.services : requestDetails?.services || [];
    const services = sourceServices
      .map((svc) => {
        const service = svc.service?._id || svc.service;
        const subService = svc.subService?._id || svc.subService;
        const serviceItem = svc.serviceItem?._id || svc.serviceItem;
        const serviceBody = { service, subService, serviceItem };

        if (svc.startTime) serviceBody.startTime = svc.startTime;
        if (svc.endTime) serviceBody.endTime = svc.endTime;
        if (svc.startDate) serviceBody.startDate = svc.startDate;
        if (svc.endDate) serviceBody.endDate = svc.endDate;
        if (svc.singleDate || svc.date) serviceBody.startDate = svc.singleDate || svc.date;
        if (svc.frequencyMap || svc.customFrequencyMap) serviceBody.frequencyMap = svc.frequencyMap || svc.customFrequencyMap;

        return serviceBody;
      })
      .filter((s) => s.service);

    const rawAge = requestDetails?.agePreference;
    const agePreference = {
      min: rawAge?.min > 0 ? rawAge.min : 18,
      max: rawAge?.max > 0 && rawAge.max < 100 ? rawAge.max : 65,
    };

    const address = accumulated.address ?? sanitizeAddress(requestDetails?.address);
    const description = accumulated.description ?? requestDetails?.description;
    const startDate = accumulated.startDate || firstVisit.startDate || firstVisit.singleDate || firstVisit.date || requestDetails?.startDate;
    const endDate = accumulated.endDate || firstVisit.endDate || requestDetails?.endDate;
    const frequencyMap = accumulated.frequencyMap || firstVisit.frequencyMap || firstVisit.customFrequencyMap || requestDetails?.frequencyMap;

    const body = {
      services,
      patientCondition: accumulated.patientCondition ?? requestDetails?.patientCondition ?? "",
      distanceInKm: accumulated.distanceInKm ?? requestDetails?.distanceInKm ?? 1,
      languagePreferences: accumulated.languagePreferences?.length ? accumulated.languagePreferences : requestDetails?.languagePreferences?.length ? requestDetails.languagePreferences : ["English"],
      genderPreferences: accumulated.genderPreferences?.length ? accumulated.genderPreferences : requestDetails?.genderPreferences?.length ? requestDetails.genderPreferences : ["Male"],
      nationality: requestDetails?.nationality || "Vietnamese",
      agePreference,
      price: String(accumulated.price ?? ""),
      quotationURL: uploadedQuotationURL,
    };

    if (address) body.address = address;
    if (description) body.description = description;
    if (startDate) body.startDate = startDate;
    if (endDate) body.endDate = endDate;
    if (frequencyMap) body.frequencyMap = frequencyMap;

    dispatch(verifyNurseRequest({ id, body })).then((result) => {
      if (result.meta.requestStatus === "fulfilled") {
        nav(strings.navigation.manageNurseReq);
      } else {
        import("../../../../widgets/Toast/toastPopup").then(({ default: ToastPopup }) => ToastPopup.error(result.error?.message || "Failed to send quote"));
      }
    });
  };

  const handleStepChange = (stepIndex) => {
    if (enabledSteps.includes(stepIndex)) {
      setCurrentStep(stepIndex);
      setPage(stepIndex);
    }
  };

  return (
    <div className="service-details-container">
      <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />
      <div className="department-heading">{t("generateQuotation")}</div>
      <Widgets.ProgressBar steps={steps} currentStep={currentStep} enabledSteps={enabledSteps} onStepChange={handleStepChange} />

      {page === 1 && <PatientDetails setPage={setPage} setEnabledSteps={setEnabledSteps} data={patientDetailsData} onReview={handleStep1Review} onNext={handleStep1Next} />}

      {page === 2 && <ServiceConfiguration setPage={setPage} setEnabledSteps={setEnabledSteps} initialServices={requestDetails?.services || []} onSave={handleStep2Save} />}

      {page === 3 && <NursePreferences setPage={setPage} setEnabledSteps={setEnabledSteps} onSave={handleStep3Save} />}

      {page === 4 && (
        <QuotationDetails
          setPage={setPage}
          visitsData={formData.visits || []}
          distanceInKm={formData.distanceInKm ?? patientDetailsData.distanceInKm}
          onFinalSubmit={handleFinalSubmit}
        />
      )}
    </div>
  );
};

export default ServiceDetails;
